import { FileMeta } from './types'
import ExcelJS from 'exceljs'

/** @param {File} file */
export function getFileMeta(file) {
  const fileName = file.name

  const fileType = String(fileName).split('.').pop()
  const supportedFormats = ['csv', 'xlsx', 'json']

  if (!supportedFormats.some((x) => fileType === x)) {
    throw new Error('正しい形式のファイルをアップロードしてください')
  }

  let type = 'csv'
  if (fileType === 'xlsx') {
    type = 'excel'
  } else if (fileType === 'json') {
    type = 'json'
  }

  return FileMeta.parse({
    name: fileName,
    size: file.size,
    type
  })
}

/**
 * @function convertTableToObject
 * @param {string[][]} records
 * @returns {Record<string, unknown>[]}
 */
export function convertTableToObject(records) {
  const recordLength = records.length
  if (!recordLength) return []

  const headers = records[0]
  const output = new Array(recordLength - 1)

  for (let i = 1; i < recordLength; i++) {
    const row = records[i]
    const obj = {}
    for (let j = 0; j < row.length; j++) {
      obj[headers[j]] = row[j]
    }
    output[i - 1] = obj
  }

  return output
}

/** @function escapeCsv */
const escapeCsv = (value) => {
  if (value == null) return ''
  const str = String(value)
  if (str.includes('"') || str.includes(',') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * @function convertToCsvString
 * @param {Record<string, unknown>[]} records
 */
export function convertToCsvString(records) {
  if (!records || records.length === 0) return ''

  const headers = Object.keys(records[0])
  const recordsLength = records.length
  const headerLength = headers.length

  const _records = new Array(records.length + 1)

  _records[0] = headers.join(',')

  for (let i = 0; i < recordsLength; i++) {
    const record = records[i]
    const row = new Array(headerLength)

    for (let j = 0; j < headerLength; j++) {
      row[j] = escapeCsv(record[headers[j]])
    }

    _records[i + 1] = row.join(',')
  }

  return _records.join('\n')
}

/**
 * @function downloadCsv
 * @param {string} filename
 * @param {string} csvContent
 */
export function downloadCsv(filename, csvContent) {
  // Excelでの文字化け防止のためBOM付きUTF-8で出力する
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  if (link.download !== undefined) {
    // Check for download attribute support
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link) // Clean up the element
    URL.revokeObjectURL(url) // Release the object URL
  } else {
    // Fallback for browsers that don't support the download attribute
    window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(BOM + csvContent))
  }
}

/**
 * @function downloadExcel
 * @param {string} filename
 * @param {Record<string, unknown>[]} records
 */
export async function downloadExcel(filename, records) {
  if (!records || records.length === 0) {
    throw new Error('エクスポートするデータがありません')
  }

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Sheet1')

  // ヘッダー行を追加
  const headers = Object.keys(records[0])
  worksheet.addRow(headers)

  // データ行を追加
  records.forEach((record) => {
    const row = headers.map((header) => record[header] ?? '')
    worksheet.addRow(row)
  })

  // ヘッダー行のスタイルを設定
  const headerRow = worksheet.getRow(1)
  headerRow.font = { bold: true }
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  }

  // 列幅を自動調整
  worksheet.columns.forEach((column) => {
    let maxLength = 0
    column.eachCell({ includeEmpty: true }, (cell) => {
      const columnLength = cell.value ? cell.value.toString().length : 10
      if (columnLength > maxLength) {
        maxLength = columnLength
      }
    })
    column.width = maxLength < 10 ? 10 : maxLength + 2
  })

  // ファイルをダウンロード
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * @function readJsonFile
 * @param {File} file
 * @returns {Promise<Record<string, unknown>[]>}
 */
export async function readJsonFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const text = e.target?.result
        if (!text) {
          reject(new Error('ファイルの読み込みに失敗しました'))
          return
        }

        const jsonData = JSON.parse(String(text))

        // 配列形式のJSONを想定
        if (!Array.isArray(jsonData)) {
          reject(new Error('JSONファイルは配列形式である必要があります'))
          return
        }

        if (jsonData.length === 0) {
          reject(new Error('JSONファイルにデータが含まれていません'))
          return
        }

        resolve(jsonData)
      } catch (error) {
        reject(new Error(`JSONの解析に失敗しました: ${error.message}`))
      }
    }

    reader.onerror = () => {
      reject(new Error('ファイルの読み込み中にエラーが発生しました'))
    }

    reader.readAsText(file, 'UTF-8')
  })
}

/**
 * @function convertJsonToTableFormat
 * @param {Record<string, unknown>[]} jsonData
 * @returns {{ Headers: string[]; Records: string[][] }}
 */
export function convertJsonToTableFormat(jsonData) {
  if (!jsonData || jsonData.length === 0) {
    return { Headers: [], Records: [] }
  }

  // 最初のオブジェクトからヘッダーを取得
  const headers = Object.keys(jsonData[0])

  // データを行形式に変換
  const records = jsonData.map((item) => {
    return headers.map((header) => {
      const value = item[header]
      // null/undefinedの場合は空文字に変換
      return value == null ? '' : String(value)
    })
  })

  return {
    Headers: headers,
    Records: records
  }
}
